import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { logout } from '../utils/api';
import { ChevronDown } from 'lucide-react';

interface MainHeaderProps {
  onUploadBackground?: () => void;
  onAddLayer?: () => void;
  onExport?: () => void;
}

const MainHeader: React.FC<MainHeaderProps> = ({
  onUploadBackground,
  onAddLayer,
  onExport,
}) => {
  const { user, isAdmin, checkAuth } = useAuth();
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [editMenuOpen, setEditMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      // セッション状態を更新
      checkAuth();
      // ホームページにリダイレクト
      window.location.href = '/';
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  // Project メニューを表示/非表示
  const toggleProjectMenu = () => {
    setProjectMenuOpen(!projectMenuOpen);
  };

  // クリックアウェイでメニューを閉じる
  const closeProjectMenu = () => {
    setProjectMenuOpen(false);
  };

  const closeEditMenu = () => {
    setEditMenuOpen(false);
  };

  React.useEffect(() => {
    // Projectメニューのイベントリスナー
    if (projectMenuOpen) {
      document.addEventListener('click', closeProjectMenu);
    } else {
      document.removeEventListener('click', closeProjectMenu);
    }

    // Editメニューのイベントリスナー
    if (editMenuOpen) {
      document.addEventListener('click', closeEditMenu);
    } else {
      document.removeEventListener('click', closeEditMenu);
    }

    return () => {
      document.removeEventListener('click', closeProjectMenu);
      document.removeEventListener('click', closeEditMenu);
    };
  }, [projectMenuOpen, editMenuOpen]);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 h-12">
      <div className="w-full h-full px-4">
        <div className="flex justify-between items-center h-full">
          {/* 左側のナビゲーション */}
          <div className="flex items-center space-x-4">
            {/* ロゴ */}
            <Link
              to="/"
              className="font-medium text-white text-sm bg-gray-800 px-3 py-1.5 rounded hover:bg-gray-700 transition duration-150"
            >
              JimonGenerator
            </Link>

            {/* Project ドロップダウン */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                className="flex items-center text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded px-2 py-1 transition duration-150"
                onClick={toggleProjectMenu}
              >
                Project
                <ChevronDown size={16} className="ml-1" />
              </button>

              {projectMenuOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-lg rounded-md py-1 z-10 min-w-32">
                  <button
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition duration-150"
                    onClick={() => {
                      // 将来的に実装
                      setProjectMenuOpen(false);
                    }}
                  >
                    New
                  </button>
                  <button
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition duration-150"
                    onClick={() => {
                      // 将来的に実装
                      setProjectMenuOpen(false);
                    }}
                  >
                    Save
                  </button>
                </div>
              )}
            </div>

            {/* Edit ドロップダウン */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                className="flex items-center text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded px-2 py-1 transition duration-150"
                onClick={() => setEditMenuOpen(!editMenuOpen)}
              >
                Edit
                <ChevronDown size={16} className="ml-1" />
              </button>

              {editMenuOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-lg rounded-md py-1 z-10 min-w-32">
                  {onUploadBackground && (
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition duration-150"
                      onClick={() => {
                        onUploadBackground();
                        setEditMenuOpen(false);
                      }}
                    >
                      Upload Image
                    </button>
                  )}
                  {onAddLayer && (
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition duration-150"
                      onClick={() => {
                        onAddLayer();
                        setEditMenuOpen(false);
                      }}
                    >
                      New Layer
                    </button>
                  )}
                  {onExport && (
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition duration-150"
                      onClick={() => {
                        onExport();
                        setEditMenuOpen(false);
                      }}
                    >
                      Export
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* 管理者のみ表示 */}
            {/* {user && isAdmin() && (
              <Link
                to="/admin"
                className="text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded px-2 py-1 transition duration-150"
              >
                Admin
              </Link>
            )} */}
          </div>

          {/* 右側のログイン情報 */}
          <div className="flex items-center">
            {user ? (
              <div className="flex items-center space-x-3">
                <span className="text-xs text-gray-500">{user.email}</span>
                <button
                  onClick={handleLogout}
                  className="text-xs text-gray-700 hover:text-red-600 hover:bg-red-50 rounded px-2 py-1 transition duration-150"
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="text-xs text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded px-2 py-1 transition duration-150"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default MainHeader;
